'use client';
import React, { useCallback, useEffect } from 'react';
import type { SafeType } from '@benzinga/safe-await';
import type { Authentication } from '@benzinga/session';
import { isMobile } from '@benzinga/device-utils';
import { raptiveAdManager } from '@benzinga/ads-utils';

import { OptimizelyRegPaywall } from '../../components/AuthIterations/AccountCreationIterations/OptimizelyRegPaywall';
import type { AuthContainerV2Props } from '../types';
import { useAuthTracking } from '../hooks/useAuthTracking';
import { emitAuthSuccess } from '../utils/emitAuthSuccess';

/**
 * Sophi-driven inline regwall. Wraps OptimizelyRegPaywall.
 * Does NOT redirect on success — emits `benzinga:auth:success` so parents unlock in place.
 */
export const RegwallSurface: React.FC<AuthContainerV2Props> = ({
  email,
  enableRaptivePaywallMode = true,
  onSuccess,
  placement,
}) => {
  useAuthTracking({ surface: 'regwall' });

  useEffect(() => {
    enableRaptivePaywallMode && raptiveAdManager.enablePaywallMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegister = useCallback(
    (auth: SafeType<Authentication>, _authFor: string) => {
      const user = auth?.ok?.user;
      if (!user || user.accessType === 'anonymous') return;

      const detail = {
        action: 'register' as const,
        isNewUser: true,
        method: 'email' as const,
        surface: 'regwall' as const,
        userId: user.id ? String(user.id) : undefined,
      };

      emitAuthSuccess(detail);
      onSuccess?.(detail, auth);
    },
    [onSuccess],
  );

  return (
    <OptimizelyRegPaywall
      allowClose={false}
      authMode="register"
      email={email}
      isMobile={isMobile()}
      onRegister={handleRegister}
      placement={placement}
    />
  );
};

RegwallSurface.displayName = 'RegwallSurface';
